import React from 'react';
import { Check, Close } from '@icon-park/react';
import { useEditorStore } from '../../store/useEditorStore';
import { MATERIAL_OPTIONS, getMaterialConfig } from '../../utils/wallMaterialConfig';
import { useTranslation } from 'react-i18next';

interface WallPropertyPanelProps {
  position: { x: number; y: number };
  onConfirm: () => void;
  onCancel: () => void;
}

const WallPropertyPanel: React.FC<WallPropertyPanelProps> = ({
  position,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const {
    wallProperties,
    setWallThickness,
    setWallHeight,
    setWallMaterial,
    setWallColor,
    setWallOpacity,
    setCustomAngle,
    toggleUseCustomAngle
  } = useEditorStore();

  // 获取当前材质的配置
  const currentMaterialConfig = getMaterialConfig(wallProperties.material as any);

  return (
    <div
      className="absolute bg-white rounded shadow-lg border border-gray-300 p-2 z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '180px',
      }}
    >
      {/* 材质选择 */}
      <div className="mb-1.5">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.material')}
        </label>
        <select
          value={wallProperties.material}
          onChange={(e) => setWallMaterial(e.target.value)}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
        >
          {MATERIAL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(`materials.${option.value}`, option.label)}
            </option>
          ))}
        </select>
        <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
          <span>{t('property.thickness')}</span>
          <span className="text-primary-600">{currentMaterialConfig.thickness}mm</span>
          <span className="text-gray-300">|</span>
          <span>{t('property.opacity')}</span>
          <span className="text-primary-600">{Math.round(currentMaterialConfig.opacity * 100)}%</span>
        </div>
      </div>

      {/* 颜色选择 */}
      <div className="mb-1.5">
        <label className="block text-xs text-gray-600 mb-0.5 flex items-center gap-1">
          <span>{t('elementProps.color')}</span>
          <span className="w-3 h-3 rounded border border-gray-300" style={{ backgroundColor: wallProperties.color }}></span>
        </label>
        <input
          type="color"
          value={wallProperties.color}
          onChange={(e) => setWallColor(e.target.value)}
          className="w-full h-7 border border-gray-300 rounded cursor-pointer"
          title={wallProperties.color}
        />
      </div>

      {/* 透明度滑块 */}
      <div className="mb-1.5">
        <div className="flex items-center justify-between mb-0.5">
          <label className="block text-xs text-gray-600">{t('elementProps.opacity')}</label>
          <span className="text-xs text-gray-500">{Math.round(wallProperties.opacity * 100)}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={wallProperties.opacity}
          onChange={(e) => setWallOpacity(Number(e.target.value))}
          className="w-full"
        />
      </div>

      {/* 墙厚设置 */}
      <div className="mb-1.5">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.thickness')}
        </label>
        <input
          type="number"
          value={wallProperties.thickness}
          onChange={(e) => setWallThickness(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="50"
          max="1000"
          step="10"
        />
      </div>

      {/* 3D渲染高度设置 */}
      <div className="mb-1.5">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.height3d')}
        </label>
        <input
          type="number"
          value={wallProperties.height}
          onChange={(e) => setWallHeight(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="50"
          max="1000"
          step="25"
        />
        <div className="text-[10px] text-gray-400 mt-0.5">{t('elementProps.logicHeight')}</div>
      </div>

      {/* 自定义角度设置 */}
      <div className="mb-1.5">
        <label className="flex items-center gap-1 text-xs text-gray-600 mb-0.5">
          <input
            type="checkbox"
            checked={wallProperties.useCustomAngle}
            onChange={(e) => toggleUseCustomAngle(e.target.checked)}
            className="w-3 h-3"
          />
          <span>{t('elementProps.customAngle')}</span>
        </label>
        {wallProperties.useCustomAngle && (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={wallProperties.customAngle || 0}
              onChange={(e) => setCustomAngle(Number(e.target.value))}
              className="flex-1 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
              min="0"
              max="360"
              step="1"
              placeholder={t('elementProps.anglePlaceholder')}
            />
            <span className="text-xs text-gray-500">°</span>
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-1">
        <button
          onClick={onConfirm}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 transition-colors"
        >
          <Check size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.confirm')}</span>
        </button>
        <button
          onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors"
        >
          <Close size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.cancel')}</span>
        </button>
      </div>
    </div>
  );
};

export default WallPropertyPanel;
